import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "w-full" }
const _hoisted_2 = { class: "text-left text-gray-400" }
const _hoisted_3 = { class: "py-1.5 pr-3" }
const _hoisted_4 = { class: "py-1.5 pr-3" }
const _hoisted_5 = { class: "py-1.5 pr-3" }
const _hoisted_6 = { class: "py-1.5 pr-3" }
const _hoisted_7 = {
  class: "py-1.5 pr-3",
  colspan: "2"
}
const _hoisted_8 = { class: "inline-flex items-center gap-1" }
const _hoisted_9 = {
  type: "button",
  class: "rounded-full p-1 transition-colors hover:bg-gray-100"
}
const _hoisted_10 = { class: "sr-only" }
const _hoisted_11 = { class: "py-1.5 align-top" }
const _hoisted_12 = { class: "py-1.5 pr-2 align-top font-medium text-gray-500" }
const _hoisted_13 = { class: "w-44 py-1.5 pr-3 align-top" }
const _hoisted_14 = { class: "w-44 py-1.5 pr-3 align-top" }
const _hoisted_15 = {
  key: 0,
  class: "mt-0.5 text-[10px] italic text-gray-400"
}
const _hoisted_16 = { class: "py-1.5 pr-3 align-top whitespace-nowrap" }
const _hoisted_17 = { class: "py-1.5 pr-3 align-top" }
const _hoisted_18 = { class: "py-1.5 pr-3 align-top" }
const _hoisted_19 = {
  key: 1,
  class: "text-gray-300"
}
const _hoisted_20 = { class: "py-1.5 align-top text-right" }
const _hoisted_21 = ["disabled", "aria-label", "onClick"]
const _hoisted_22 = {
  key: 0,
  class: "border-l-2 border-red-400 bg-red-50"
}
const _hoisted_23 = {
  colspan: "7",
  class: "py-1.5 pr-4 text-xs font-medium text-red-800"
}
const _hoisted_24 = {
  key: 1,
  class: "border-l-2 border-red-400 bg-red-50"
}
const _hoisted_25 = {
  colspan: "7",
  class: "py-1.5 pr-4 text-xs font-medium text-red-800"
}
const _hoisted_26 = {
  key: 2,
  class: "border-l-2 border-amber-400 bg-amber-50"
}
const _hoisted_27 = {
  colspan: "7",
  class: "py-1.5 pr-4 text-xs text-amber-800"
}

import { computed, ref } from 'vue';
import { addDays, format, parseISO } from 'date-fns';
import { Trash2 } from 'lucide-vue-next';
import DatePicker from '@/components/ui/DatePicker.vue';
import CustomCheckbox from '@/components/ui/CustomCheckbox.vue';
import Badge from '@/components/ui/badge/Badge.vue';
import InfoIcon from '@/components/icons/InfoIcon.vue';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import useSortableTable from '@/composables/useSortableTable';
import { ProductionGroup, type TableHeader } from '@/types';
import {
    durationDays,
    formatDuration,
    invalidRangeSuggestionIds,
    overlappingSuggestionIds,
    type BackfillDraft,
    type BackfillDraftConfidence,
    type BackfillDraftSource,
} from './useProductionGroupBackfill';


export default /*@__PURE__*/_defineComponent({
  __name: 'BackfillSuggestionTable',
  props: {
    suggestions: {},
    existingGroups: {},
    today: {},
    disabled: { type: Boolean }
  },
  emits: ["setAccepted", "discard", "setStart", "setEnd"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// "#" stays each suggestion's original detection-order number regardless of sort direction - it is
// the same number the groups-lane chart bands are labelled with (BackfillTimeline builds its band
// labels off this same array's order), so a row must keep its number when sorting reorders the rows
// around it. Numbering first and sorting the numbered array (rather than sorting suggestions and
// numbering after) is what keeps that number pinned to its row.
const numberedSuggestions = computed(() => props.suggestions.map((suggestion, index) => ({ suggestion, number: index + 1 })));

// Same sort-cycle composable (asc -> desc -> off) every other sortable table header in the app uses.
// ISO ("yyyy-MM-dd") start dates sort correctly as plain strings under its default comparator.
const startHeader: TableHeader = { text: '', value: 'suggestion.start', sortable: true };
const { sortedItems: displayedSuggestions, getHeaderSortIcon, onHeaderClicked } = useSortableTable(numberedSuggestions, ref([startHeader]));

const overlappingIds = computed(() => overlappingSuggestionIds(props.suggestions, props.existingGroups, props.today));
const invalidRangeIds = computed(() => invalidRangeSuggestionIds(props.suggestions));

function isBlocked(id: string) {
    return invalidRangeIds.value.has(id) || overlappingIds.value.has(id);
}

function dayAfter(date: string) {
    return format(addDays(parseISO(date), 1), 'yyyy-MM-dd');
}

const latestStart = computed(() => Math.max(...props.suggestions.map((s) => Date.parse(s.start))));

// End is only ever editable to "ongoing" (blank) on the most recent suggestion - an earlier one
// left open-ended would silently overlap whatever comes after it.
function isLatest(suggestion: BackfillDraft) {
    return Date.parse(suggestion.start) === latestStart.value;
}

function formatDate(value: string) {
    return format(parseISO(value), 'yyyy-MM-dd');
}

// The inverse of formatDate: DatePicker displays its `value` via `new Date(value)`, which reads a
// bare "yyyy-MM-dd" as UTC midnight - the wrong calendar day once formatted back in a negative-offset
// timezone. parseISO instead reads a date-only string as local midnight, so round-tripping it through
// toISOString() hands DatePicker a timestamp that displays as the same local day it was given.
function toPickerValue(dateOnly: string): string {
    return parseISO(dateOnly).toISOString();
}

function sourceBadgeVariant(source: BackfillDraftSource) {
    return source === 'Manual' ? 'outline' : 'secondary';
}

// Same green/amber/red reading as the data-quality badges elsewhere in the app (see
// qualityCategoryStyles.ts) - "outline" gives a transparent base to layer these over, since the
// other variants all carry their own background.
const CONFIDENCE_BADGE_CLASS: Record<BackfillDraftConfidence, string> = {
    High: 'border-transparent bg-green-100 text-green-700',
    Medium: 'border-transparent bg-yellow-100 text-yellow-700',
    Low: 'border-transparent bg-red-100 text-red-700',
};

function confidenceBadgeClass(confidence: BackfillDraftConfidence) {
    return CONFIDENCE_BADGE_CLASS[confidence];
}

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!

  return (_openBlock(), _createElementBlock("table", _hoisted_1, [
    _createElementVNode("thead", null, [
      _createElementVNode("tr", _hoisted_2, [
        _cache[1] || (_cache[1] = _createElementVNode("th", { class: "w-10 py-1.5" }, null, -1)),
        _cache[2] || (_cache[2] = _createElementVNode("th", { class: "w-10 py-1.5 pr-2" }, "#", -1)),
        _createElementVNode("th", _hoisted_3, [
          _createElementVNode("button", {
            type: "button",
            class: "inline-flex items-center gap-1 hover:text-gray-600",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(onHeaderClicked)(startHeader)))
          }, [
            _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.table.headings.start')) + " ", 1),
            _createVNode(_component_Icon, {
              class: "h-3.5 w-2",
              src: _unref(getHeaderSortIcon)(startHeader).icon
            }, null, 8, ["src"])
          ])
        ]),
        _createElementVNode("th", _hoisted_4, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.table.headings.end')), 1),
        _createElementVNode("th", _hoisted_5, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.table.headings.duration')), 1),
        _createElementVNode("th", _hoisted_6, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.table.headings.source')), 1),
        _createElementVNode("th", _hoisted_7, [
          _createElementVNode("span", _hoisted_8, [
            _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.table.headings.confidence')) + " ", 1),
            _createVNode(_unref(TooltipProvider), { "delay-duration": 0 }, {
              default: _withCtx(() => [
                _createVNode(_unref(Tooltip), null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(TooltipTrigger), { "as-child": "" }, {
                      default: _withCtx(() => [
                        _createElementVNode("button", _hoisted_9, [
                          _createElementVNode("span", _hoisted_10, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.table.confidenceExplanation')), 1),
                          _createVNode(InfoIcon, {
                            class: "size-4",
                            "aria-hidden": "true"
                          })
                        ])
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(TooltipContent), {
                      class: "max-w-64",
                      "collision-padding": 12
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("p", null, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.table.confidenceExplanation')), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ])
    ]),
    _createElementVNode("tbody", null, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(displayedSuggestions), ({ suggestion, number }) => {
        return (_openBlock(), _createElementBlock(_Fragment, {
          key: suggestion.id
        }, [
          _createElementVNode("tr", {
            class: _normalizeClass(["border-t border-gray-100", { 'opacity-50': !suggestion.accepted, 'border-l-2 border-l-red-400 bg-red-50': isBlocked(suggestion.id) }])
          }, [
            _createElementVNode("td", _hoisted_11, [
              _createVNode(CustomCheckbox, {
                checked: suggestion.accepted,
                tabbable: "",
                disabled: _ctx.disabled,
                label: _ctx.$t('views.monitoring.groupOverview.modals.backfill.table.acceptRow', { number }),
                "label-class": "sr-only",
                "onUpdate:checked": (value) => emit('setAccepted', suggestion.id, value)
              }, null, 8, ["checked", "disabled", "label", "onUpdate:checked"])
            ]),
            _createElementVNode("td", _hoisted_12, _toDisplayString(number), 1),
            _createElementVNode("td", _hoisted_13, [
              _createVNode(DatePicker, {
                value: toPickerValue(suggestion.start),
                resetable: false,
                "aria-label": _ctx.$t('views.monitoring.groupOverview.modals.backfill.table.headings.start'),
                max: _ctx.today,
                position: "above",
                "show-overlay": false,
                disabled: _ctx.disabled || !suggestion.accepted,
                "onUpdate:value": (value) => emit('setStart', suggestion.id, formatDate(value))
              }, null, 8, ["value", "aria-label", "max", "disabled", "onUpdate:value"])
            ]),
            _createElementVNode("td", _hoisted_14, [
              _createVNode(DatePicker, {
                value: suggestion.end ? toPickerValue(suggestion.end) : '',
                resetable: isLatest(suggestion),
                "aria-label": _ctx.$t('views.monitoring.groupOverview.modals.backfill.table.headings.end'),
                min: dayAfter(suggestion.start),
                max: _ctx.today,
                position: "above",
                "show-overlay": false,
                disabled: _ctx.disabled || !suggestion.accepted,
                "onUpdate:value": (value) => emit('setEnd', suggestion.id, value ? formatDate(value) : null)
              }, null, 8, ["value", "resetable", "aria-label", "min", "max", "disabled", "onUpdate:value"]),
              (!suggestion.end)
                ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.table.ongoing')), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("td", _hoisted_16, _toDisplayString(_unref(formatDuration)(_unref(durationDays)(suggestion.start, suggestion.end || _ctx.today))), 1),
            _createElementVNode("td", _hoisted_17, [
              _createVNode(Badge, {
                variant: sourceBadgeVariant(suggestion.source)
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t(`views.monitoring.groupOverview.modals.backfill.sources.${suggestion.source}`)), 1)
                ]),
                _: 2
              }, 1032, ["variant"])
            ]),
            _createElementVNode("td", _hoisted_18, [
              (suggestion.confidence)
                ? (_openBlock(), _createBlock(Badge, {
                    key: 0,
                    variant: "outline",
                    class: _normalizeClass(confidenceBadgeClass(suggestion.confidence))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t(`views.monitoring.groupOverview.modals.backfill.confidence.${suggestion.confidence}`)), 1)
                    ]),
                    _: 2
                  }, 1032, ["class"]))
                : (_openBlock(), _createElementBlock("span", _hoisted_19, "—"))
            ]),
            _createElementVNode("td", _hoisted_20, [
              _createElementVNode("button", {
                type: "button",
                class: "rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-600 disabled:cursor-default disabled:opacity-50",
                disabled: _ctx.disabled,
                "aria-label": _ctx.$t('views.monitoring.groupOverview.modals.backfill.table.discard'),
                onClick: ($event: any) => (emit('discard', suggestion.id))
              }, [
                _createVNode(_unref(Trash2), {
                  class: "size-4",
                  "aria-hidden": "true"
                })
              ], 8, _hoisted_21)
            ])
          ], 2),
          (invalidRangeIds.value.has(suggestion.id))
            ? (_openBlock(), _createElementBlock("tr", _hoisted_22, [
                _cache[3] || (_cache[3] = _createElementVNode("td", null, null, -1)),
                _createElementVNode("td", _hoisted_23, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.table.invalidRangeWarning')), 1)
              ]))
            : (overlappingIds.value.has(suggestion.id))
              ? (_openBlock(), _createElementBlock("tr", _hoisted_24, [
                  _cache[4] || (_cache[4] = _createElementVNode("td", null, null, -1)),
                  _createElementVNode("td", _hoisted_25, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.modals.backfill.table.overlapWarning')), 1)
                ]))
              : _createCommentVNode("", true),
          (suggestion.note)
            ? (_openBlock(), _createElementBlock("tr", _hoisted_26, [
                _cache[5] || (_cache[5] = _createElementVNode("td", null, null, -1)),
                _createElementVNode("td", _hoisted_27, _toDisplayString(suggestion.note), 1)
              ]))
            : _createCommentVNode("", true)
        ], 64))
      }), 128))
    ])
  ]))
}
}

})